'use client';

import * as React from 'react';
import { useCallback } from 'react';
import { XIcon } from 'lucide-react';

import { Command as CommandPrimitive } from 'cmdk';
import { Command } from './Command';
import { Badge } from './Badge';
import { PiPlus } from 'react-icons/pi';
import { Button } from './Button';
import { cn } from '@ais-ui/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './Tooltip';

type ListInputProps = {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  /**
   * Optional display-only formatter for each chip. The underlying stored value
   * is never changed — this only affects how an item is rendered (e.g. spacing
   * out a phone number). Defaults to the identity function.
   */
  formatDisplay?: (value: string) => string;
  disabled?: boolean;
  disabledReason?: string;
};

export const ListInput = React.forwardRef<HTMLDivElement, ListInputProps>(
  (
    {
      value,
      onChange,
      placeholder,
      formatDisplay = (v) => v,
      disabled = false,
      disabledReason = '',
    },
    _ref,
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);

    const [inputValue, setInputValue] = React.useState('');

    React.useEffect(() => {
      if (disabled) {
        setInputValue('');
      }
    }, [disabled]);

    const handleUnselect = React.useCallback(
      (deselected: string) => {
        if (disabled) return; // Don't allow removal when disabled
        onChange(
          value ? value.filter((valueItem) => valueItem !== deselected) : [],
        );
      },
      [value, onChange, disabled],
    );

    const onAdd = useCallback(() => {
      if (disabled) return;
      if (inputValue.trim()) {
        onChange(value ? [...value, inputValue.trim()] : [inputValue.trim()]);
        setInputValue('');
      }
    }, [value, inputValue, onChange, disabled]);

    const handleBlur = useCallback(() => {
      onAdd();
    }, [onAdd]);

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        const input = inputRef.current;
        if (input) {
          if (e.key === 'Delete' || e.key === 'Backspace') {
            if (input.value === '' && !disabled) {
              onChange(value ? value.slice(0, -1) : []); // Removes the last item from the array
            }
          }
          // This is not a default behaviour of the <input /> field
          if (e.key === 'Escape') {
            input.blur();
          }

          if (e.key === 'Enter') {
            onAdd();
          }
        }
      },
      [onChange, value, onAdd, disabled],
    );

    return (
      <Command
        onKeyDown={handleKeyDown}
        className="overflow-visible bg-transparent"
      >
        <div
          className={cn(
            'group border border-input px-3 py-1 text-sm rounded-md shadow-xs',
            disabled && 'cursor-not-allowed',
          )}
        >
          <div className="flex gap-1 flex-wrap items-center">
            {value.map((valueItem) => {
              return (
                <Badge key={valueItem} variant="secondary">
                  {formatDisplay(valueItem)}
                  {!disabled && (
                    <button
                      type="button"
                      className="ml-1 ring-offset-background rounded-full outline-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleUnselect(valueItem);
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={() => handleUnselect(valueItem)}
                    >
                      <XIcon className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                    </button>
                  )}
                </Badge>
              );
            })}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="flex-1 ml-2"
                    tabIndex={disabled && disabledReason ? 0 : undefined}
                    aria-disabled={disabled}
                  >
                    <CommandPrimitive.Input
                      ref={inputRef}
                      disabled={disabled}
                      value={inputValue}
                      onValueChange={setInputValue}
                      onBlur={handleBlur}
                      placeholder={placeholder}
                      className="bg-transparent outline-none placeholder:text-muted-foreground w-full disabled:cursor-not-allowed"
                    />
                  </div>
                </TooltipTrigger>
                {disabled && disabledReason && (
                  <TooltipContent>
                    <p>{disabledReason}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 flex group/btn p-0"
              disabled={disabled}
              onClick={() => onAdd()}
            >
              <PiPlus className="group-hover/btn:text-primary"></PiPlus>
            </Button>
          </div>
        </div>
      </Command>
    );
  },
);
